import React, {useEffect, useMemo, useState} from "react";
import {TableRow} from "@material-ui/core";
import AnnualReportTableCell from "../common/AnnualReportTableCell";
import PropTypes from "prop-types";
import Tr from "translations/Translations";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import SwapVerticalCircleIcon from '@material-ui/icons/SwapVerticalCircle';
import WorkableIconButton from "components/button/WorkableIconButton";
import {getMoveAnnualReportEntryConfig} from "../common/MoveAnnualReportEntry";
import clsx from "clsx";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import WorkableIconExpandButton from "components/button/WorkableIconExpandButton";
import WorkableIconErrorButton from "components/button/WorkableIconErrorButton";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import {simpleConfirmNoTranslate} from "libs/ConfirmNotification";
import AnnualReportsService from "services/AnnualReportsService";
import {useParams} from "react-router";
import useSnackbar from "hooks/useSnackbar";
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import IconButton from "components/button/IconButton";
import {getAnnualReportEditPopup} from "../common/AnnualReportEditPopup";
import scrollTo from "libs/scrollTo";

function AnnualReportTablePart(
    {
        type,
        reportStep,
        partName,
        classes,
        disabled,
        entries,
        search,
        setEditPopup,
        setMovePopup,
        columnCount,
        getRowValues,
    }
) {
    const params = useParams();
    const [expanded, setExpanded] = useState(false);
    const [hasIncomplete, setHasIncomplete] = useState(false);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const entriesFilteres = useMemo(() => {
        if (search.search) {
            return entries.filter((entry) => entry.number.includes(search.search))
        }
        return entries;
    }, [entries, search])

    useEffect(() => {
        setHasIncomplete(entries.some(entry => !entry.complete))
    }, [entries])

    useDidUpdateEffect(() => {
        if (!expanded && search.search && entries.some((entry) => entry.number.includes(search.search))) {
            setExpanded(true);
        }
    }, [search])

    const doDelete = (entry) => {
        simpleConfirmNoTranslate(Tr("confirm_delete_annual_entry", {number: entry.number}), () => {
            AnnualReportsService.deleteAnnualReportTableEntry(params.id, entry.identity, reportStep, type).then((result) => {
                if (result) {
                    enqueueSuccessSnackbar(Tr("deleted") + " " + entry.number)
                }
            })
        })
    }

    const changeComplete = (entry, complete) => {
        const data = {
            ...entry,
            complete
        }
        AnnualReportsService.saveAnnualReportTableEntry(params.id, reportStep, data)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        enqueueSuccessSnackbar(Tr("saved") + " " + entry.number)
                    } else {
                        setEditPopup(getAnnualReportEditPopup(entry, false, true))
                    }
                }

            })
    }

    return (<>
            <TableRow className={classes.variantRow}>
                <AnnualReportTableCell borderLeft colSpan={columnCount - 1} onClick={() => setExpanded(!expanded)}>
                    {`${partName} (${entriesFilteres.length}/${entries.length})`}
                </AnnualReportTableCell>
                <AnnualReportTableCell onClick={() => setExpanded(!expanded)}>
                    {
                        hasIncomplete && <WorkableIconErrorButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(true);
                                const incompleteEntry = entriesFilteres.find(x => !x.complete);
                                if (incompleteEntry) {
                                    setTimeout((identity) => scrollTo(`#${reportStep}-${identity}`, 200), 100, incompleteEntry.identity)
                                }
                            }}
                            tooltip={Tr("annual_report_section_incomplete")}
                            className={classes.sectionErrorIcon}

                        />
                    }
                </AnnualReportTableCell>
                <AnnualReportTableCell borderRight colSpan={2}>
                    <WorkableIconExpandButton expanded={expanded} onClick={() => setExpanded(!expanded)}/>
                </AnnualReportTableCell>
            </TableRow>
            {expanded && entriesFilteres.map((entry) => (
                <TableRow id={`${reportStep}-${entry.identity}`} key={entry.identity}
                          className={clsx(type, classes.demandsRow, {complete: entry.complete, incomplete: !entry.complete})}>

                    {
                        getRowValues(entry).map((x, i) => <AnnualReportTableCell borderLeft key={i}>{x}</AnnualReportTableCell>)
                    }

                    <AnnualReportTableCell borderLeft className={classes.buttonColumn}>
                        <WorkableIconEditButton disabled={disabled} onClick={() => setEditPopup(getAnnualReportEditPopup(entry))}/>
                        {
                            entry.complete ?
                                <IconButton disabled={disabled} tooltip={Tr("mark_draft_version")} Icon={CheckCircleOutlineIcon}
                                            onClick={() => changeComplete(entry, false)}/>
                                :
                                <IconButton disabled={disabled} tooltip={Tr("mark_saved_version")} Icon={RadioButtonUncheckedIcon}
                                            onClick={() => changeComplete(entry, true)}/>
                        }
                    </AnnualReportTableCell>
                    <AnnualReportTableCell borderRight className={classes.buttonColumn}>
                        <WorkableIconButton
                            disabled={disabled}
                            Icon={SwapVerticalCircleIcon}
                            tooltip={Tr("change_order_type")}
                            onClick={() => setMovePopup(getMoveAnnualReportEntryConfig(params.id, entry.identity, type))}
                        />
                        <WorkableIconDeleteButton
                            disabled={disabled}
                            onClick={() => doDelete(entry)}
                        />
                    </AnnualReportTableCell>
                </TableRow>
            ))
            }
            {expanded && !entriesFilteres.length && <TableRow>
                <AnnualReportTableCell className={classes.noOptions} borderRight borderLeft colSpan={columnCount + 2}>
                    {Tr("no_options")}
                </AnnualReportTableCell>
            </TableRow>}
        </>
    );
}

AnnualReportTablePart.propTypes = {
    type: PropTypes.string.isRequired,
    reportStep: PropTypes.string.isRequired,
    partName: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
    disabled: PropTypes.bool.isRequired,
    entries: PropTypes.array.isRequired,
    search: PropTypes.shape({
        search: PropTypes.string
    }).isRequired,
    setEditPopup: PropTypes.func.isRequired,
    setMovePopup: PropTypes.func.isRequired,
    columnCount: PropTypes.number.isRequired,
    getRowValues: PropTypes.func.isRequired
}


export default AnnualReportTablePart;